import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Button,
  IndexTable,
  Pagination,
  Spinner,
  Divider,
  Modal,
  TextField,
  FormLayout,
  Checkbox,
  Select,
  Tooltip,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useJobToggles } from '../hooks/useJobToggles';
import SkuMapTable from '../components/SkuMapTable';
import RetailerCatalogueTable from '../components/RetailerCatalogueTable';
import BrandConnectionTable from '../components/BrandConnectionTable';
import StoredRetailerOrdersTable from '../components/StoredRetailerOrdersTable';
import { headingsFor, emptyRow, itemCountFor, DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';
import { trackingPushUnavailable } from '../utils/tracking-push';

// Page size for the cross-marketplace orders table (also sent as the API `limit`).
//
// Now the SEED for `orderPageSize` rather than the size itself: that table pages SERVER-side,
// so the count an admin picks has to go out as the request's `limit` and the request has to
// re-fire. Slicing the response would show 10 of the 20 rows the server sent while the label
// counted pages from the full total. The Retailer Accounts and Brands tables on this page are
// the other kind — their whole array is already here, so they slice locally — and each of the
// three keeps its OWN size, because they are read separately.
const GLOBAL_ORDERS_PAGE_SIZE = DEFAULT_PAGE_SIZE;

/**
 * Column definitions for the two tables on this page, so an EMPTY table still tells an
 * admin what will appear in it.
 *
 * Both tables previously replaced themselves with a single sentence when there was nothing
 * to show, which on a fresh install is the whole screen: no column names, no shape, no way
 * to know what adding an account would produce. IndexTable cannot fix that alone — at
 * itemCount 0 it swaps the WHOLE table for emptyState, headings included — so the pattern
 * is itemCount={rows.length || 1} plus one synthetic row spanning every column. Same as
 * SkuMapTable, QueueTable and the Tracking page.
 *
 * `tip` becomes IndexTable's own tooltipContent. Several of these columns are not
 * self-explanatory, and two are outright counter-intuitive — "Verified" is a stored
 * timestamp rather than a live check, and "Marketplace" decides billing attribution.
 */
export const ACCOUNT_COLUMNS = [
  { key: 'name', title: 'Name', tip: "The name this account is filed under in 2W, prefilled from what Mirakl reported, with its slug beneath. Renaming it applies here and to future orders only — orders already billed keep the old name, so reporting will show both." },
  // The tag and what it bills as, on the account that owns them. They used to appear
  // only on each brand's Connector tab, which put the identity of a RETAILER in a per-brand row.
  // "Order Tag", not "Order Tag Prefix". The value is used BOTH ways — shopify-order-service.js
  // writes it as a standalone tag AND prefixed to a second tag carrying the retailer's order
  // id — so "prefix" was not wrong, it just named the less useful of the two. What matters on
  // this screen is attribution, and the pattern created with the account is patternType EXACT
  // on this value (admin-connector.js:1341-1352), so what decides the commission rate is the
  // WHOLE tag. "Prefix" also sends an admin searching Shopify for this-plus-something when the
  // value on its own is a tag they can search for directly. The DB column stays orderTagPrefix.
  { key: 'prefix', title: 'Order Tag', tip: 'The tag written onto every Shopify order created from this retailer, and what attribution matches on — so changing it changes how future orders are billed. It defaults to the account name, and is also prefixed to a second tag carrying the retailer’s own order id.' },
  // Beside the tag deliberately: they are one word apart and do entirely different jobs, so
  // the two tips have to be readable together. Note the precedence runs the OTHER WAY from the
  // tag's — a brand may override this one.
  { key: 'orderNamePrefix', title: 'Order Name Prefix', tip: 'What Shopify orders from this retailer are CALLED — "SPO" names an order #SPO-6672623108156-A. It applies to every brand on this account that has not set its own on its connection, and only to orders created from now on. Nothing to do with the Order Tag beside it, which decides what an order bills as. Left empty, each order keeps the reference the retailer sends with it.' },
  // "Marketplace", because that is what this cell holds and it is NOT the same thing as the
  // retailer account the row is. A row here is a Mirakl account; the value here is the
  // marketplace on it that the tag bills as — ShopSimon, Macy's. Calling it "Bills As" named
  // the consequence and left the thing itself unnamed, so the one word on the screen that
  // distinguishes the account from what it sells through was missing.
  //
  // The key stays `resolves` — it is the API's own name for the verdict, not a label — and
  // the tip keeps the consequence, which the title no longer states.
  { key: 'resolves', title: 'Marketplace', tip: 'Which marketplace this tag currently bills as, and therefore which commission rate its orders are charged at. Matched against the patterns on Marketplace Settings. A tag matching nothing bills as Others, at the wrong rate.' },
  { key: 'status', title: 'Status', tip: 'Whether this account is active. An inactive account keeps its data and its history but is skipped by every scheduled job.' },
  { key: 'brands', title: 'Brands', tip: 'How many 2W brands are connected to this retailer account. Several brands routinely share one account, which is why most figures here are per account rather than per brand.' },
  // Products beside SKUs everywhere a SKU count appears — one product carries many SKUs,
  // so "198 SKUs" alone does not say how much of a brand's catalogue actually reached the retailer.
  // Tooltip rewritten. The first version said "counted only for SKUs already matched back to
  // a Shopify product, so it can lag the SKU count" — which described a transient lag for a
  // figure that was structurally always 0, and so papered over the defect rather than
  // disclosing it. The query is fixed (admin-connector.js) and this now says where the number
  // comes from and the one condition under which it is legitimately 0.
  { key: 'products', title: 'Products', tip: 'Distinct Shopify products behind those listed SKUs, resolved through the brand’s own Shopify catalogue — a product with several listed SKUs counts once. Mirakl only ever tells us a SKU, never a product, so this reads 0 until that brand’s Shopify catalogue has been synced.' },
  { key: 'skus', title: 'SKUs', tip: 'SKU listings we have read from this retailer. One SKU listed at this retailer counts once here — this column is already per account.' },
  // No Orders column. It counted every order this retailer ever sent, with no period
  // attached, so it grew forever and never answered a question — "orders from when?" was
  // the objection on Aug 5. Per-period counts live on Analytics, where a range is selectable.
  { key: 'verified', title: 'Verified', tip: 'When the credentials were last proved against this retailer, by reading one offer from it. It is a stored timestamp, not a live check — the account may have stopped working since.' },
  { key: 'actions', title: 'Actions', tip: 'Runs one operation for this account now instead of waiting for the schedule. Fetching orders and offers only reads. Pushing inventory writes, and refuses while its toggle is off in Sync Settings.' },
];

/**
 * Client-side sorting for Retailer Accounts.
 *
 * The accounts list is one unpaginated array from getConnectorMarketplaces(), so sorting is
 * done here rather than by adding a route: there is no second page for a sort to be wrong about.
 *
 * WHICH COLUMNS. The seven that carry a value an admin would order by. `verified` and
 * `actions` stay unsortable — the first is three different shapes of state (failing, a date,
 * never checked) that no single order reads correctly, the second is buttons.
 *
 * EACH SORT USES THE VALUE THE CELL SHOWS, not the field behind it, so the order always
 * matches what is on screen. `prefix` falls back to the account name exactly as the cell (and
 * the tag builder) does, and `status` sorts on the badge's own words.
 *
 * TRAP, and the reason SORT_TOGGLE_LABELS below is DERIVED rather than typed out: on a
 * sortable column Polaris reads sortToggleLabels[index][direction] UNGUARDED
 * (IndexTable.js:477). A missing entry for a sortable index is a TypeError at render, and
 * omitting the prop entirely silently discards every tooltipContent on the table — and every
 * column here has a tip that has to survive. Building the map from ACCOUNT_COLUMNS means a
 * column cannot be made sortable without also getting its labels.
 */
/**
 * Said once, used by both the Add and the Edit field below. The edit copy adds a sentence the
 * add copy cannot: on a new account there is nothing to rename.
 */
const ORDER_NAME_PREFIX_HELP = 'What Shopify orders from this retailer are called — "SPO" '
  + 'names an order #SPO-6672623108156-A. Every brand on this account uses it unless that '
  + 'brand sets its own on its connection. Leave it empty and each order keeps the reference '
  + 'the retailer sends with it.';

/**
 * Why Push inventory is unavailable for one retailer, or null.
 *
 * TWO gates, because the route enforces two and the button knew about one: the system-wide
 * Push inventory setting, and the retailer's OWN Sync inventory checkbox. With the account's
 * switch off the button stayed live, opened its confirmation, and only then took a 409 —
 * "a button that can only fail is worse than no button", which is the rule the rest of this
 * screen already follows.
 *
 * The account's reason is worded as the route words its refusal, so the tooltip and the error
 * an admin might still reach say the same thing rather than sounding like two problems.
 */
// The per-order Push tracking rule lives with the other shared helpers so the brand's own order
// list can use it too; re-exported here because this page was its home.
export { trackingPushUnavailable };

export const inventoryPushUnavailable = (account, switchedOffReasonFor) => {
  const globalReason = switchedOffReasonFor('pushInventoryEnabled', 'Push inventory');
  if (globalReason) return globalReason;
  if (account?.syncInventory === false) {
    return `Inventory sync is switched off for ${account.name}. Turn it on with Edit on this `
      + 'row — the system-wide setting alone is not enough.';
  }
  return null;
};

const ACCOUNT_SORT_LABELS = {
  name: { ascending: 'A-Z', descending: 'Z-A' },
  prefix: { ascending: 'A-Z', descending: 'Z-A' },
  orderNamePrefix: { ascending: 'A-Z', descending: 'Z-A' },
  resolves: { ascending: 'A-Z', descending: 'Z-A' },
  status: { ascending: 'Active first', descending: 'Inactive first' },
  brands: { ascending: 'Fewest first', descending: 'Most first' },
  products: { ascending: 'Fewest first', descending: 'Most first' },
  skus: { ascending: 'Fewest first', descending: 'Most first' },
};

/**
 * What each sortable column orders by, read off the row markup below.
 *
 * `resolves` returns null for the two states that resolve to NOTHING — no resolution reported
 * at all ("—"), and a prefix that matches no pattern ("No match → Others"). Those two are the
 * rows an admin is looking for, and null puts them together at the end in both directions
 * instead of scattering them among the retailer names they do not have. "Pattern inactive"
 * does have a pattern, so it sorts as the words the cell shows.
 *
 * The three counts return 0, not null, where the field is absent: the cell renders `?? 0`, so
 * 0 IS the displayed value, and treating it as missing would move a row the admin can see a
 * zero on.
 */
export const ACCOUNT_SORT_VALUES = {
  name: (mp) => mp.name,
  prefix: (mp) => mp.orderTagPrefix || mp.name,
  // NULL, not the name. Unlike the tag beside it this has no display fallback — an account
  // with none shows "Order's own reference" — so inventing one here would sort a row by a
  // value that is nowhere on its row.
  orderNamePrefix: (mp) => mp.orderNamePrefix || null,
  resolves: (mp) => {
    const r = mp.tagResolution;
    if (!r) return null;
    if (r.state === 'ok') return r.marketplace;
    if (r.state === 'inactive') return 'Pattern inactive';
    return null;
  },
  status: (mp) => (mp.isActive ? 'Active' : 'Inactive'),
  brands: (mp) => mp._count?.brandConnections ?? 0,
  products: (mp) => mp.productCount ?? 0,
  skus: (mp) => mp._count?.skuMaps ?? 0,
};

/** Per-heading flags, positional: index here is the index Polaris reports to onSort. */
export const ACCOUNT_SORTABLE = ACCOUNT_COLUMNS.map((c) => Boolean(ACCOUNT_SORT_VALUES[c.key]));

/** Complete by construction — see the trap above. */
export const ACCOUNT_SORT_TOGGLE_LABELS = ACCOUNT_COLUMNS.reduce((acc, c, i) => {
  if (ACCOUNT_SORTABLE[i]) acc[i] = ACCOUNT_SORT_LABELS[c.key];
  return acc;
}, {});

/** Absent, unknown or blank. Sorted to the END in both directions, so nothing missing reads as small. */
const isBlankSortValue = (v) => v === null || v === undefined || v === '';

/**
 * Numbers numerically, strings case-insensitively.
 *
 * `numeric: true` so a name ending in a number ("Store 10" vs "Store 9") orders the way an
 * admin reads it; the counts never reach it as strings, but a lexical fallback on them would
 * put "10" before "9" and that is exactly the bug this guards.
 */
const compareSortValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });
};

/**
 * The Brands card's columns.
 *
 * NO "Orders" COLUMN. It was `brand._count.miraklOrders` — every order that brand has ever
 * received, with no period attached. That is precisely the figure removed from the Retailer
 * Accounts table above, for the reason recorded in ACCOUNT_COLUMNS: it grows forever and
 * never answers a question, and "orders from when?" was the objection on Aug 5. The table
 * below was missed at the time. Per-period counts live on Analytics, where a range is
 * selectable, and Retailer Orders below lists the orders themselves.
 */
const GLOBAL_ORDER_COLUMNS = [
  { key: 'miraklId', title: 'Mirakl ID', tip: "The retailer's own id for the order. A retailer can split one customer purchase across several of these." },
  { key: 'marketplace', title: 'Marketplace', tip: 'Which retailer account the order came from.' },
  { key: 'brand', title: 'Brand', tip: "The 2W brand this order's lines were attributed to. Only this brand's slice of the retailer's order is held on the row." },
  { key: 'status', title: 'Status', tip: "2W's own state for the order: whether we have created it in Shopify, refused it, or not got to it yet." },
  { key: 'miraklStatus', title: 'Mirakl Status', tip: "The retailer's own state for the order, as of the last time we read it. It does not change on its own between reads." },
  // The two columns that make this list usable while testing: which Shopify order an
  // ingested order became, and why one did not become any. Both were already in the
  // payload and neither was shown, so a FAILED row said only that something went wrong.
  { key: 'shopifyOrder', title: 'Shopify Order', tip: 'The Shopify order we created for it, or the reason we could not. Empty means nothing has been created yet.' },
  { key: 'items', title: 'Items', tip: "Line count for this brand's slice of the order, not for the customer's whole purchase." },
  { key: 'total', title: 'Total', tip: "The retailer's own total for this brand's lines, in the order's own currency." },
  { key: 'date', title: 'Date', tip: 'When the retailer placed the order, as the retailer reported it — not when we imported it. A dash means the retailer sent no order date, which is rare; the order is still real.' },
  { key: 'actions', title: 'Actions', tip: 'Runs one Mirakl or Shopify operation for this single order now. Writes to the retailer refuse while their toggle is off in Sync Settings.' },
];

/**
 * Retailer Orders sorts in the DATABASE, not here.
 *
 * That table is paginated server-side over every order on every account, so reordering the
 * twenty rows on screen would present a sort of the page as a sort of the list. The heading
 * click sends a key; mirakl-order-service.js whitelists it and orders the query.
 *
 * A column key IS the key the service accepts, for eight of the nine. The ninth is the first
 * column: its key here is `miraklId` — the heading id, and the key its sort wording is filed
 * under — while the service whitelists the name of the column ON THE ROW, `miraklOrderId`
 * (mirakl-order-service.js:870). An unrecognised key is not an error there: `_orderOrderBy`
 * returns DEFAULT_ORDER for anything it does not know. So the mismatch cost nothing visible
 * except the sort itself — the arrow moved to the Mirakl ID heading, the rows did not, and the
 * arrow then misdescribed the ordering on every page.
 *
 * OVERRIDES ONLY, so the eight keys that already agree keep agreeing by construction and a
 * column added later needs no entry. The override lives here rather than as a renamed column
 * key because the service's whitelist is not the console's to change, and because `miraklId`
 * is also the heading id and the key ORDER_SORT_WORDS is written against.
 */
const ORDER_SORT_API_KEYS = {
  miraklId: 'miraklOrderId',
};

const ORDER_SORT_KEYS = GLOBAL_ORDER_COLUMNS
  .filter((c) => c.key !== 'actions')
  .map((c) => ORDER_SORT_API_KEYS[c.key] || c.key);

const ORDER_SORTABLE = GLOBAL_ORDER_COLUMNS.map((c) => c.key !== 'actions');

/**
 * REQUIRED, and required COMPLETE. Polaris reads sortToggleLabels[index][direction]
 * unguarded, so a partial map throws at render; with the prop absent entirely it returns the
 * bare sort button and silently discards every tooltipContent. Derived from the columns so a
 * column added later cannot arrive without its label.
 *
 * The wording says what the sort does, not which way an arrow points.
 */
const ORDER_SORT_WORDS = {
  miraklId: { ascending: 'A-Z', descending: 'Z-A' },
  marketplace: { ascending: 'A-Z', descending: 'Z-A' },
  brand: { ascending: 'A-Z', descending: 'Z-A' },
  status: { ascending: 'A-Z', descending: 'Z-A' },
  miraklStatus: { ascending: 'A-Z', descending: 'Z-A' },
  shopifyOrder: { ascending: 'In Shopify first', descending: 'Not yet in Shopify first' },
  items: { ascending: 'Fewest items first', descending: 'Most items first' },
  total: { ascending: 'Lowest total first', descending: 'Highest total first' },
  date: { ascending: 'Oldest first', descending: 'Newest first' },
};

const ORDER_SORT_TOGGLE_LABELS = Object.fromEntries(
  GLOBAL_ORDER_COLUMNS
    .map((c, i) => [i, ORDER_SORT_WORDS[c.key]])
    .filter(([, words]) => Boolean(words)),
);

// headingsFor and emptyRow now live in utils/table.jsx — one copy for the whole console.

// The MiraklOrderStatus enum (2w-connect/web/prisma/schema.prisma:1198-1205), in pipeline order.
// No endpoint publishes it, so this is the one client-side copy; it must stay complete or the
// missing members become unfilterable while their rows still render.
const MIRAKL_ORDER_STATUSES = [
  'PENDING',
  'SYNCED',
  'FULFILLED',
  'TRACKING_PUSHED',
  'FAILED',
  'SKIPPED',
];

// Label map for the values above (presentation, not data).
const MIRAKL_STATUS_LABELS = {
  PENDING: 'Pending',
  SYNCED: 'Synced',
  FULFILLED: 'Fulfilled',
  TRACKING_PUSHED: 'Tracking Pushed',
  FAILED: 'Failed',
  SKIPPED: 'Skipped',
};

// New-marketplace form defaults.
// rateLimitGapMs / requestTimeoutMs are deliberately absent: they used to be seeded with 200 and
// 30000, which disagreed with MiraklMarketplace.rateLimitGapMs @default(1000) (schema.prisma:911)
// and with mirakl-client.js:377 (MIRAKL_RATE_LIMIT_GAP_MS || 1000). Three sources of truth for a
// throttling value that governs whether we get rate-limited by the retailer is two too many, so
// the console no longer supplies one and the server default applies. An admin who wants a
// specific gap can still type it - the fields are left in the form.
// Rate limit, timeout and notes are gone from this form, both on add and on edit. All three
// have server defaults, and nobody knows the right throttle for a retailer before connecting
// to it. The columns still exist and are still honoured — they are simply not something this
// screen asks for.
/**
 * The same Tooltip + subdued InfoIcon used on the Analytics metric cards, so an info dot means
 * one thing across the console.
 *
 * Copy rule for everything passed in here: describe what the admin is looking at, not how it is
 * stored or which job produced it. Table names, job names and Mirakl's own API codes are
 * implementation detail an admin has no way to act on.
 */
// InfoTip moved to components/InfoTip.jsx — one of three identical copies.

const EMPTY_MARKETPLACE = {
  name: '',
  slug: '',
  apiUrl: '',
  apiKey: '',
  // The tag every order from this retailer carries, and what billing attributes on. Kept on
  // the ACCOUNT rather than per brand connection: the pattern that interprets it is global, so
  // five brands on one retailer cannot drift into five different tags.
  orderTagPrefix: '',
  orderNamePrefix: '',
  // Creation floor for the order ingest, defaulted to TODAY for a new account.
  //
  // With no floor the first sweep asks the retailer for its entire order history, and the
  // sweep fires within seconds of the next worker restart or settings save — so the unsafe
  // case was also the automatic one. Every historical shipped or delivered order is written
  // PENDING, and order creation has no date bound of its own, so that backlog drains into
  // Shopify the day creation is switched on.
  //
  // Pre-filled rather than left blank so the safe window is the default while still being
  // visible and editable: clearing it is a deliberate act, which is the right way round.
  ingestOrdersCreatedAfter: new Date().toISOString().slice(0, 10),
  // Which syncs run for this retailer. All on by default; the account-wide Deactivate is the
  // all-or-nothing switch, and these are the three concerns that fail independently.
  syncOrders: true,
  syncInventory: true,
  syncTracking: true,
  // Ticked on a new retailer, like the three sync flags above: a retailer is added in order to
  // take its orders and bring them into Shopify, so the form starts from that and an exception
  // is unticked before saving.
  //
  // These two are writes, and neither has a system-wide gate behind it — the connector settings
  // columns that used to hold one are retired. acceptOrders tells the live retailer 2W will
  // fulfil the order; createShopifyOrders makes a real, billable Shopify order. What bounds a
  // new account is ingestOrdersCreatedAfter above: only orders created from that date are
  // ingested, so only those can reach either path.
  acceptOrders: true,
  createShopifyOrders: true,
  orderAcceptanceMode: null,
};

export default function Connector() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [marketplaces, setMarketplaces] = useState([]);
  // Retailer Accounts sort. `null` index means "as the API returned them", which is what the
  // table shows until an admin clicks a heading — a default sort would silently reorder a
  // screen nobody asked to reorder.
  const [accountSortIndex, setAccountSortIndex] = useState(null);
  const [accountSortDirection, setAccountSortDirection] = useState('ascending');
  // Retailer Accounts paging, also client-side — getConnectorMarketplaces returns every
  // account. Its own size, kept apart from the Brands table's directly below it.
  const [accountPage, setAccountPage] = useState(1);
  const [accountPageSize, setAccountPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [globalOrders, setGlobalOrders] = useState({ orders: [], total: 0, page: 1, totalPages: 1 });
  const [orderPage, setOrderPage] = useState(1);
  // Seeded with this table's own existing count, so nobody's view changes size unasked.
  const [orderPageSize, setOrderPageSize] = useState(GLOBAL_ORDERS_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // Which write operations Sync Settings allows, so Push inventory explains itself when it is
  // switched off rather than looking live and failing on click. The route refuses either way.
  const { switchedOffReason } = useJobToggles();
  // Deferred refreshes, cleared on unmount.
  //
  // These are the "check back in 5 seconds" refetches after queueing a background job. Left
  // uncleared they fire after the admin has navigated away — React 18 swallows the setState,
  // so what is left is a pointless request, and on a slow connection several of them.
  const timers = useRef([]);
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current = []; }, []);
  const later = (fn, ms) => { timers.current.push(setTimeout(fn, ms)); };

  /**
   * The accounts in display order.
   *
   * Copied before sorting — sorting `marketplaces` in place would mutate state, and React
   * would not re-render for it. Array.sort is stable, so rows that tie keep the order the API
   * gave them rather than shuffling on every click.
   */
  const sortedMarketplaces = useMemo(() => {
    const column = accountSortIndex === null ? null : ACCOUNT_COLUMNS[accountSortIndex];
    const valueOf = column ? ACCOUNT_SORT_VALUES[column.key] : null;
    if (!valueOf) return marketplaces;
    const direction = accountSortDirection === 'descending' ? -1 : 1;
    return [...marketplaces].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      const aBlank = isBlankSortValue(av);
      const bBlank = isBlankSortValue(bv);
      // Blank last regardless of direction, so it is never mistaken for a smallest value.
      if (aBlank || bBlank) return aBlank && bBlank ? 0 : aBlank ? 1 : -1;
      return direction * compareSortValues(av, bv);
    });
  }, [marketplaces, accountSortIndex, accountSortDirection]);

  // Polaris hands back the heading index and the direction it wants; both are ours to store.
  const handleAccountSort = useCallback((index, direction) => {
    setAccountSortIndex(index);
    setAccountSortDirection(direction);
  }, []);

  /**
   * The page of each client-side table, CLAMPED to the pages that exist.
   *
   * Both arrays are refetched — fetchData runs again after every account action, deactivation,
   * deletion and attribution fix — so either can shrink under a page that was valid when it was
   * chosen. An out-of-range slice is empty, and the empty slice would render the "no retailer
   * accounts yet" row over a screen that still has accounts on it.
   *
   * Sliced off the SORTED array, never the raw one: sorting the page instead would reorder the
   * ten rows on screen while the heading claimed to have sorted the table.
   */
  const accountTotalPages = Math.max(1, Math.ceil(sortedMarketplaces.length / accountPageSize));
  const accountSafePage = Math.min(accountPage, accountTotalPages);
  const visibleMarketplaces = sortedMarketplaces.slice(
    (accountSafePage - 1) * accountPageSize,
    accountSafePage * accountPageSize,
  );

  // A new row count, or a NEW SORT, starts again at page 1. The point of sorting is to bring
  // the row you want onto the first page, and staying on page 4 lands in the middle of the new
  // order; and page 4 of a ten-row window is inside page 1 of a fifty-row one.
  useEffect(() => {
    setAccountPage(1);
  }, [accountPageSize, accountSortIndex, accountSortDirection]);


  // Bumped to make the SKU comparison table refetch without re-mounting it.
  const [skuRowsToken, setSkuRowsToken] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Marketplace modal state
  /**
   * What the RETAILER requires, read from its own Mirakl platform settings when the account
   * was connected or last re-verified. Returns a sentence when there is no decision for the
   * admin to make, and null when there is — the caller shows a switch in that case.
   *
   * AUTOMATIC is the only value that removes the choice: the retailer accepts for us, so a
   * switch here would do nothing. Everything else keeps the switch, including a retailer
   * nobody has checked — an unread value must never be shown as "no acceptance needed",
   * because that is how orders end up waiting with nothing set to accept them.
   */
  const acceptanceNote = (mode) => (mode === 'AUTOMATIC'
    ? 'This retailer accepts orders itself, so there is nothing to switch on here.'
    : null);

  /** The caveat that belongs under the switch, where there is one. */
  const acceptanceHelp = (mode) => {
    if (mode === 'NOT_AUTOMATIC') {
      return 'This retailer requires every order to be accepted. While this is off, orders '
        + 'arrive and wait — and a retailer cancels an order that waits too long.';
    }
    if (mode === 'BOTH') {
      return 'This retailer allows either mode and does not report which applies to your '
        + 'account, so treat it as needing acceptance until you know otherwise.';
    }
    return 'Not checked yet — press Re-verify to read what this retailer requires. '
      + 'Accepting an order commits you to fulfilling it.';
  };

  const [mpModalOpen, setMpModalOpen] = useState(false);
  const [mpForm, setMpForm] = useState(EMPTY_MARKETPLACE);
  const [editingMpId, setEditingMpId] = useState(null);
  const [mpSaving, setMpSaving] = useState(false);
  // Adding an account is two steps: connect, then confirm what answered. `mpProbe` holds
  // what Mirakl said and is what gates the second step — until it exists there is nothing
  // to name the account after.
  const [mpProbe, setMpProbe] = useState(null);
  const [mpProbing, setMpProbing] = useState(false);
  const [mpProbeError, setMpProbeError] = useState('');

  /**
   * DELETING A RETAILER ACCOUNT — the most destructive action on this screen.
   *
   * `deleteTarget` is the whole account row, not just an id: the dialog names the account,
   * compares what is typed against `name`, and reads `_count.brandConnections` off it to say
   * up front whether the route will even accept the delete.
   *
   * Stats are fetched when the dialog opens rather than read from the row, because the row's
   * counts are the LIST payload's and a dialog that says how much is about to be destroyed
   * should say it from a fresh read. Until they arrive the confirm button stays disabled —
   * see `deleteBlockedReason`.
   */
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [deleteStats, setDeleteStats] = useState(null);
  const [deleteStatsError, setDeleteStatsError] = useState('');
  const [deleteStatsLoading, setDeleteStatsLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);


  // Create the attribution pattern for an ACCOUNT's prefix. The account is where the prefix
  // lives now, so this is where the fix belongs.
  const [fixingAccount, setFixingAccount] = useState(null);
  const handleAccountAttribution = async (mpId) => {
    setFixingAccount(mpId);
    setError('');
    try {
      const res = await apiClient.createAccountAttribution(mpId);
      if (res?.success) setSuccess(res.message || 'Attribution pattern created');
      else setError(res?.error || 'Could not create the attribution pattern');
    } catch (err) {
      setError(err.message || 'Could not create the attribution pattern');
    }
    setFixingAccount(null);
    fetchData();
  };

  // Orders filter
  const [orderFilter, setOrderFilter] = useState({
    marketplace: '', brand: '', status: '', search: '',
  });
  // Debounced separately from the committed filter so typing does not fire a request per
  // keystroke against a table that pages server-side.
  const [orderSearchInput, setOrderSearchInput] = useState('');
  // null = no sort chosen, so the list arrives in the API's own order (newest first) rather
  // than being reordered before anyone asks.
  const [orderSortIndex, setOrderSortIndex] = useState(null);
  const [orderSortDirection, setOrderSortDirection] = useState('descending');

  // Read by fetchData, which must not depend on the row count: it also loads brands and
  // accounts, and taking the size as a dependency would refetch all three every time someone
  // changed the orders table's size. Same pattern as QueueTable's filters ref.
  const orderPageSizeRef = useRef(GLOBAL_ORDERS_PAGE_SIZE);
  orderPageSizeRef.current = orderPageSize;

  const fetchData = useCallback(async () => {
    try {
      // Queue metrics are no longer fetched here. The queue became a table that reads
      // /connector/queue/jobs itself — which returns the same counts alongside the rows, so
      // fetching them separately meant two requests that could disagree with each other for
      // a poll interval — and the table has since moved to its own page entirely.
      // getSkuMapStats is no longer called. Its two figures were the unscoped Total SKUs and
      // Brands pair removed below, and nothing else on this page used them — leaving the
      // request in place would have kept a round trip on every load to fill state that is
      // never read.
      const [brandsData, mpData, ordersData] = await Promise.all([
        apiClient.getConnectorBrands(),
        apiClient.getConnectorMarketplaces(),
        apiClient.getGlobalConnectorOrders({ limit: orderPageSizeRef.current, page: 1 }),
      ]);
      setBrands(brandsData || []);
      setMarketplaces(mpData || []);
      setGlobalOrders(ordersData || { orders: [], total: 0, page: 1, totalPages: 1 });
    } catch (err) {
      setError('Failed to load connector data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSkuRefresh = async () => {
    setRefreshing(true);
    setError('');
    try {
      // The SERVER'S message, not a fixed string. It reports how many accounts were queued
      // and says plainly when none was — this asserted success either way.
      const res = await apiClient.refreshSkuMap();
      setSuccess(res?.message || 'SKU map refresh queued.');
      later(() => {
        fetchData();
        // The table keeps its own paging, filters and sort, so it refetches on a token rather
        // than being re-mounted — which would drop the admin back to page 1.
        setSkuRowsToken((n) => n + 1);
      }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to trigger SKU refresh');
    }
    setRefreshing(false);
  };

  const openMpModal = (mp = null) => {
    setMpProbe(null);
    setMpProbeError('');
    if (mp) {
      setEditingMpId(mp.id);
      setMpForm({
        name: mp.name,
        slug: mp.slug,
        apiUrl: mp.apiUrl,
        apiKey: '',
        orderTagPrefix: mp.orderTagPrefix || '',
      orderNamePrefix: mp.orderNamePrefix || '',
        // Date-only for the input. Empty when unset, which is a real state — an existing
        // account has no floor — and must not be defaulted to today on edit: that would
        // silently narrow a window someone is already relying on.
        ingestOrdersCreatedAfter: mp.ingestOrdersCreatedAfter
          ? new Date(mp.ingestOrdersCreatedAfter).toISOString().slice(0, 10)
          : '',
        // ?? true, not || true: a stored false must survive being loaded into the form.
        syncOrders: mp.syncOrders ?? true,
        syncInventory: mp.syncInventory ?? true,
        syncTracking: mp.syncTracking ?? true,
        // Ours, and off unless it was deliberately turned on.
        acceptOrders: mp.acceptOrders === true,
        createShopifyOrders: mp.createShopifyOrders === true,
        // Theirs, read-only — carried into the form only so the switch beside it can say
        // what this retailer requires. It is never sent back.
        orderAcceptanceMode: mp.orderAcceptanceMode ?? null,
      });
    } else {
      setEditingMpId(null);
      setMpForm(EMPTY_MARKETPLACE);
    }
    setMpModalOpen(true);
  };

  /**
   * Step one of adding an account: ask the credentials who they are.
   *
   * The name this returns is not cosmetic — it becomes the Shopify order tag and the
   * attribution pattern that decides an order's commission rate. It is taken EXACTLY as
   * Mirakl gives it, symbols included: "ShopSimon®" stays "ShopSimon®", because the tag and
   * the pattern are generated from this one string and will always agree with each other.
   * Cleaning it up here would only make it disagree with the retailer's own portal.
   */
  const handleMpProbe = async () => {
    setMpProbing(true);
    setMpProbeError('');
    setMpProbe(null);
    try {
      const result = await apiClient.probeConnectorMarketplace({
        apiUrl: mpForm.apiUrl.trim(),
        apiKey: mpForm.apiKey,
      });
      if (!result?.success) {
        setMpProbeError(result?.error || 'Could not reach this Mirakl account');
      } else {
        setMpProbe(result);
        setMpForm((f) => ({
          ...f,
          // Left empty when PC01 gave nothing, so the admin is asked rather than handed a
          // guess. tenant_name is already slug-shaped, so it makes a better slug than
          // anything derived from a display name.
          name: result.platformName || '',
          slug: result.tenantName || '',
          // Defaults to the retailer's own name, which is what the tag builder falls back to
          // anyway — so leaving it alone is the same as not setting it.
          orderTagPrefix: result.platformName || '',
          // What this retailer requires, so the acceptance switch below explains itself on
          // the screen where the account is being added rather than only after a Re-verify.
          orderAcceptanceMode: result.orderAcceptanceMode ?? null,
        }));
      }
    } catch (err) {
      setMpProbeError(err.message || 'Could not reach this Mirakl account');
    }
    setMpProbing(false);
  };

  // The throttling coercion that used to live here is gone with the fields. Nothing in this
  // form is numeric any more, so the server's defaults apply by simple omission.
  const buildMpPayload = () => ({
    ...mpForm,
    // Passed through from the probe so the account is identified on its first render rather
    // than after the first nightly verify.
    ...(mpProbe ? {
      platformName: mpProbe.platformName,
      tenantName: mpProbe.tenantName,
      platformTimezone: mpProbe.platformTimezone,
      // Only on create, and only from the probe. The update route refuses this field
      // outright — it is what the retailer reports about itself, and a screen that could
      // overwrite it would be overwriting the fact it exists to display.
      orderAcceptanceMode: mpProbe.orderAcceptanceMode,
    } : {}),
  });

  const handleMpSave = async () => {
    setMpSaving(true);
    setError('');
    try {
      if (editingMpId) {
        const payload = buildMpPayload();
        // Drop the key when it was NOT retyped, which is both the blank and the masked
        // case. This used to test only `startsWith('***')` — but openMpModal sets
        // apiKey:'' when editing, so the untouched state is empty, never masked. The
        // guard therefore checked for a case that never happens, an empty string went up
        // as a real value, and the route wrote it: editing a retailer's name or URL
        // silently erased its API key, behind a green "Marketplace updated" toast. That
        // is how Macy's lost its key, and the next sync for it fails at _ensureConfigured.
        if (!payload.apiKey || payload.apiKey.startsWith('***')) {
          delete payload.apiKey;
        }
        await apiClient.updateConnectorMarketplace(editingMpId, payload);
        setSuccess('Marketplace updated');
      } else {
        const created = await apiClient.createConnectorMarketplace(buildMpPayload());
        // Verify once, here, on first connect. The client kept this case explicitly —
        // "when you add a retailer, it should be a verify and then it should show that
        // it worked" — while dropping the standing button, because typing a bad key is
        // the moment you want to be told, and after that the daily job has it.
        if (created?.id) {
          const check = await apiClient.verifyConnectorMarketplace(created.id);
          // Naming who answered, not just that someone did. A key pasted against the wrong
          // account verifies perfectly well, and until the platform name came back there was
          // nothing in the response that would have told you.
          const who = check?.platformName
            ? ` Connected to ${check.platformName}${check.tenantName ? ` (${check.tenantName})` : ''}.`
            : '';
          setSuccess(check?.success
            ? `Marketplace created and verified — ${check.totalOffers ?? 0} offers visible.${who}`
            : `Marketplace created, but the connection failed: ${check?.error || 'unknown error'}`);
        } else {
          setSuccess('Marketplace created');
        }
      }
      setMpModalOpen(false);
      fetchData();
    } catch (err) {
      setError(err.message || 'Failed to save marketplace');
    }
    setMpSaving(false);
  };

  // The per-row check is back, as ACCOUNT_ACTIONS.reverify below, and it is now the only way
  // to force one: the nightly verification job was removed. Credentials change rarely and a
  // broken one already announces itself the moment any sync fails, so a sweep that spent a
  // call on every retailer every night to usually learn nothing was not worth the schedule.
  // Verification still runs once automatically, on first connect, from handleMpSave above.

  /**
   * Per-account manual runs, one handler for all four.
   *
   * Keyed by `${mpId}:${action}` so pressing Fetch orders on one retailer does not put a spinner
   * on every button in the table.
   *
   * These bypass the Sync Settings toggles by design: the toggles govern what happens on a
   * schedule, and a button press is the deliberate act they exist to make deliberate. That is
   * how one write is exercised against a live retailer while the crons stay off — which is why
   * the two write buttons name their direction and ask first.
   */
  const [runningAction, setRunningAction] = useState(null);

  const ACCOUNT_ACTIONS = {
    fetchOrders: {
      label: 'Fetch orders',
      writes: false,
      call: (id) => apiClient.fetchAccountOrders(id),
      tooltip: 'Collects new and changed orders from this retailer. Reads only — it cannot accept anything.',
    },
    fetchOffers: {
      label: 'Fetch SKUs',
      writes: false,
      call: (id) => apiClient.fetchAccountOffers(id),
      tooltip: 'Re-reads which of your SKUs this retailer lists, and at what stock and price. Reads only.',
    },
    reverify: {
      label: 'Re-verify',
      writes: false,
      call: (id) => apiClient.verifyConnectorMarketplace(id),
      tooltip: 'Checks this retailer\'s credentials still work, re-reads what it calls itself, '
        + 'and refreshes the carrier codes it accepts. Reads only.',
      // No unavailable reason, like the other two reads. There is no longer a switch over
      // PC01 or SH21: a button an admin presses to find out whether the credentials work
      // should not be refusable by a setting they then have to go and find.
    },
    pushInventory: {
      label: 'Push inventory',
      writes: true,
      call: (id) => apiClient.pushAccountInventory(id),
      tooltip: 'WRITES to this retailer: sends the stock changes queued for it.',
      confirm: (name) => `Send queued stock levels to ${name}? This changes what the retailer shows as available.`,
      // The reason it is unavailable, when it is. Reads carry none: a paused schedule does not
      // stop an admin looking at a retailer, which is the point of the read-only posture.
      unavailable: (mp) => inventoryPushUnavailable(mp, switchedOffReason),
    },
  };

  /**
   * Verify's message, plus what happened to the carrier list.
   *
   * SH21 permits one call per retailer per day and refuses inside that window even when
   * forced, so "carriers not refreshed" is the normal answer for a second press and needs to
   * read as normal rather than as a failure. Returns null for any other action.
   */
  const carrierNote = (res) => {
    if (!res?.message || !res.carriers) return null;
    return res.carriers.refreshed
      ? `${res.message}. ${res.carriers.count} carrier code(s) cached.`
      : `${res.message}. Carrier list unchanged: ${res.carriers.reason}`;
  };

  const runAccountAction = async (mp, key) => {
    const action = ACCOUNT_ACTIONS[key];
    if (action.confirm && !window.confirm(action.confirm(mp.name))) return;

    setRunningAction(`${mp.id}:${key}`);
    setError('');
    setSuccess('');
    try {
      const res = await action.call(mp.id);
      // Verify answers 200 with `success: false` when the retailer refuses the credentials —
      // the request worked, the credentials did not. Reported as the failure it is, or a
      // green toast would say "verified" over a broken key.
      if (res && res.success === false) {
        setError(res.error || res.message || `${action.label} failed for ${mp.name}`);
      } else {
        setSuccess(carrierNote(res) || res?.message || `${action.label} queued for ${mp.name}`);
      }
    } catch (err) {
      setError(err.message || `${action.label} failed`);
    }
    setRunningAction(null);
    fetchData();
  };

  const handleDeactivate = async (mpId) => {
    try {
      await apiClient.deactivateConnectorMarketplace(mpId);
      setSuccess('Marketplace deactivated');
      fetchData();
    } catch (err) {
      setError(err.message);
    }
  };

  /**
   * Open the delete confirmation and read the account's real counts.
   *
   * This replaced a bare `confirm()` reading "this cannot be undone". That sentence was true
   * and useless: it named no quantity, so it read the same for an empty account as for one
   * carrying thousands of orders, and a window.confirm cannot be typed into. The realistic
   * failure here is a MIS-CLICK on a row action, and a dialog you dismiss with one more click
   * does not catch a mis-click.
   *
   * The stats read failing is not fatal to the dialog — it is reported and the delete stays
   * blocked, because the whole point is to state the quantity before destroying it.
   */
  const openDeleteMarketplace = async (mp) => {
    setDeleteTarget(mp);
    setDeleteTyped('');
    setDeleteStats(null);
    setDeleteStatsError('');
    setDeleteStatsLoading(true);
    try {
      const stats = await apiClient.getConnectorMarketplaceStats(mp.id);
      setDeleteStats(stats);
    } catch (err) {
      setDeleteStatsError(err.message || 'Could not read this account’s contents.');
    }
    setDeleteStatsLoading(false);
  };

  const closeDeleteMarketplace = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteTyped('');
    setDeleteStats(null);
    setDeleteStatsError('');
    setDeleteStatsLoading(false);
  };

  // The account's name as it must be typed. Trimmed, because a stray space stored on the
  // record would otherwise demand a space nobody can see on screen — but the comparison
  // against what was TYPED is exact, so "shopsimon" and "ShopSimon " both stay blocked.
  const deleteExpectedName = (deleteTarget?.name || '').trim();
  const deleteNameMatches = deleteTarget != null && deleteTyped === deleteExpectedName;

  // Connections the ROUTE counts, which is all of them regardless of `enabled` — it refuses
  // with 409 while any exist. Said in the dialog so a refusal is predicted rather than
  // discovered after typing the name out. Note this is a different number from the stats
  // endpoint's `brandCount`, which counts only the ENABLED ones.
  const deleteConnectedCount = deleteTarget?._count?.brandConnections ?? 0;

  const deleteBlockedReason = deleteConnectedCount > 0
    ? `Disconnect ${deleteConnectedCount === 1 ? 'the connected store' : `all ${deleteConnectedCount} connected stores`} first — the server refuses to delete an account that still has any.`
    : deleteStatsLoading
      ? 'Still reading what this account holds.'
      : deleteStatsError
        ? 'Cannot confirm what would be destroyed, so this is blocked.'
        : !deleteNameMatches
          ? 'Type the account name exactly to enable this.'
          : null;

  const handleDeleteMarketplace = async () => {
    if (!deleteTarget || deleteBlockedReason) return;
    const { id, name } = deleteTarget;
    // Captured before the request, so the success line can name the quantities even though
    // the dialog state is torn down on the way out.
    const removed = deleteStats || {};
    setDeleting(true);
    setError('');
    setSuccess('');
    try {
      await apiClient.deleteConnectorMarketplace(id);
      const parts = [
        `${removed.orderCount ?? 0} order${removed.orderCount === 1 ? '' : 's'}`,
        `${removed.skuCount ?? 0} SKU map row${removed.skuCount === 1 ? '' : 's'}`,
      ];
      setSuccess(`Deleted retailer account “${name}”, along with ${parts.join(' and ')}.`);
      setDeleteTarget(null);
      setDeleteTyped('');
      setDeleteStats(null);
      setDeleteStatsError('');
      fetchData();
    } catch (err) {
      // The server's own sentence, not a generic one: a 409 here names how many stores are
      // still connected, which is the only text that says what to do next.
      setError(err.message || `Could not delete ${name}`);
    }
    setDeleting(false);
  };

  /**
   * Per-order actions on the global list — the same three the brand tab offers, and the same
   * rules behind them.
   *
   * Worth having HERE and not only on a brand's tab: this is the only screen that shows every
   * retailer order across every brand, which makes it the triage list. Without actions it was
   * a triage list you could not act on — you found the stuck order here and then navigated
   * away to do something about it.
   *
   * The stage rules are deliberately identical to ConnectorTab's, so an order does not offer a
   * different action depending on which screen you found it on: accept only while the retailer
   * is still waiting, push to Shopify only once it is accepted and paid and has no Shopify
   * order yet, push tracking only once a number exists and has not already been sent.
   */
  const [orderActionId, setOrderActionId] = useState(null);

  // Null unless the operation is actually switched off — a settings call that failed must not
  // disable anything, since the route refuses on its own (see useJobToggles).
  /**
   * Why Accept is unavailable for THIS order, or null.
   *
   * Per retailer now, so it is computed per row rather than once for the page: acceptance
   * moved off Sync Settings onto the retailer, because the retailers differ on whether a
   * seller has to accept at all. The route refuses it either way; this is so the button says
   * why before it is pressed instead of failing on the click.
   */
  const acceptOffFor = (order) => (order?.marketplace?.acceptOrders === true
    ? null
    : `Accepting orders is switched off for ${order?.marketplace?.name || 'this retailer'}. `
      + 'Switch it on in that retailer\'s settings.');
  // Passed to SkuMapTable so its push controls state why they are unavailable rather than
  // looking live and being refused by the route.
  const inventoryPushOff = switchedOffReason('pushInventoryEnabled', 'Push inventory');
  const trackingOff = switchedOffReason('pushTrackingEnabled', 'Push tracking');
  /**
   * Why Push tracking is unavailable for THIS order, or null.
   *
   * Two reasons, and the per-order one is the reason this is a function rather than the plain
   * `trackingOff` string it used to be: where the RETAILER already shipped the order
   * themselves, sending ours does not add a tracking number, it REPLACES the one the customer
   * is already following. The route refuses it now; this is so the button says so rather than
   * looking live and failing at the end.
   *
   * The retailer's own number is checked first, because it is about this order rather than
   * about the system, and it is the one an admin can do nothing about by changing a setting.
   */
  const trackingOffFor = (order) => trackingPushUnavailable(order, trackingOff);
  /** Why Push to Shopify is unavailable for THIS order, or null. Per retailer, like Accept. */
  const shopifyOrdersOffFor = (order) => (order?.marketplace?.createShopifyOrders === true
    ? null
    : `Creating Shopify orders is switched off for ${order?.marketplace?.name || 'this retailer'}. `
      + 'Switch it on in that retailer\'s settings.');

  const runOrderAction = async (order, kind) => {
    const prompts = {
      accept: 'Accept this order on the retailer?\n\nThis commits you to fulfilling it. If your '
        + 'Shopify stock cannot cover it the order will be REFUSED instead, which cannot be undone.',
      shopify: null,
      tracking: 'Tell the retailer this order shipped, with its tracking number?',
    };
    if (prompts[kind] && !window.confirm(prompts[kind])) return;

    setOrderActionId(order.id);
    setError('');
    setSuccess('');
    try {
      if (kind === 'accept') {
        const res = await apiClient.acceptMiraklOrder(order.id);
        if (res?.success && res.decision === 'accepted') setSuccess(res.message);
        else if (res?.success && res.decision === 'refused') setError(res.message);
        else setError(res?.error || 'No decision was reached for this order');
      } else if (kind === 'shopify') {
        const res = await apiClient.syncSingleMiraklOrder(order.id);
        if (res?.success) setSuccess(res.message || 'Shopify order created');
        else setError(res?.error || 'Could not create the Shopify order');
      } else {
        // The route answers runOneOrder's own result: prefer `message` — `code` is an internal
        // token like `broken_link`, which is not an answer for an admin.
        const res = await apiClient.pushOrderTracking(order.id);
        if (res?.ok) setSuccess(res.message || 'Tracking sent to the retailer');
        else setError(res?.message || res?.error || 'Tracking push failed');
      }
    } catch (err) {
      setError(err.message || 'That action failed');
    }
    setOrderActionId(null);
    await fetchFilteredOrders();
  };

  /**
   * Only the NEWEST orders request may write state — the same guard, and for the same reason,
   * as SkuMapTable and QueueTable.
   *
   * The page reset is its own effect while the fetch effect depends on `orderPage`, so both
   * run in one commit: changing the row count on page 3 fires page 3 at the new limit AND
   * then page 1 at the new limit. Two requests, no cancellation, no ordering guarantee — so
   * the slower one won and page-3 rows sat under "Page 1 of 2".
   */
  const orderRequestSeq = useRef(0);

  const fetchFilteredOrders = async () => {
    const seq = ++orderRequestSeq.current;
    try {
      const params = { limit: orderPageSize, page: orderPage };
      if (orderFilter.marketplace) params.marketplace = orderFilter.marketplace;
      if (orderFilter.brand) params.brand = orderFilter.brand;
      if (orderFilter.status) params.status = orderFilter.status;
      if (orderFilter.search.trim()) params.search = orderFilter.search.trim();
      // Sent only once a heading has been clicked. The service whitelists the key and falls
      // back to its own default for anything it does not recognise.
      if (orderSortIndex !== null) {
        params.sortBy = ORDER_SORT_KEYS[orderSortIndex];
        params.sortDirection = orderSortDirection === 'ascending' ? 'asc' : 'desc';
      }
      const data = await apiClient.getGlobalConnectorOrders(params);
      if (seq !== orderRequestSeq.current) return;
      setGlobalOrders(data || { orders: [], total: 0, page: 1, totalPages: 1 });
    } catch (err) {
      if (seq !== orderRequestSeq.current) return;
      setError('Failed to load orders');
    }
  };

  // The heading has always advertised the API's `total`, but with no page control every order
  // past the first page was unreachable - the header promised a count the table could not show.
  // `page`/`totalPages` come from the same response (mirakl-order-service.js:409/429).
  useEffect(() => {
    if (!loading) fetchFilteredOrders();
  }, [orderFilter, orderPage, orderPageSize, orderSortIndex, orderSortDirection]);

  // 350ms: long enough that a typed order id is one request, short enough not to feel lagged.
  useEffect(() => {
    const t = setTimeout(() => {
      setOrderFilter((prev) => (prev.search === orderSearchInput
        ? prev : { ...prev, search: orderSearchInput }));
    }, 350);
    return () => clearTimeout(t);
  }, [orderSearchInput]);

  // Any filter change invalidates the current page number. A sort change does too: page 3
  // of one ordering is not page 3 of another, and landing there shows rows from the middle
  // of the new order as though they were the top of it.
  // A new row count belongs in the same reset, and because the effect above watches it too,
  // the change goes out as a fresh request for page 1 with the new `limit`.
  useEffect(() => { setOrderPage(1); }, [orderFilter, orderPageSize, orderSortIndex, orderSortDirection]);

  if (loading) {
    return (
      <Page title="Connector" fullWidth>
        <InlineStack align="center"><Spinner size="large" /></InlineStack>
      </Page>
    );
  }

  const mpOptions = [{ label: 'All Marketplaces', value: '' }, ...marketplaces.map((m) => ({ label: m.name, value: m.id }))];
  // Bare lists for the comparison table: it prepends its own "All retailers" / "All brands", so
  // passing the options above would give it two.
  const retailerFilterOptions = marketplaces.map((m) => ({ label: m.name, value: m.id }));
  const brandFilterOptions = brands.map((b) => ({ label: b.name, value: b.id }));
  const brandOptions = [{ label: 'All Brands', value: '' }, ...brands.map((b) => ({ label: b.name, value: b.id }))];
  // Full MiraklOrderStatus enum (schema.prisma:1198-1205). FULFILLED and TRACKING_PUSHED were
  // missing, so the two states the table already renders for late-stage orders could not be
  // filtered for - an admin could not list what had shipped or had tracking pushed.
  const statusOptions = [
    { label: 'All Statuses', value: '' },
    ...MIRAKL_ORDER_STATUSES.map((value) => ({ label: MIRAKL_STATUS_LABELS[value] || value, value })),
  ];

  return (
    <Page title="Connector — Mirakl Integration" fullWidth>
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>
          </Layout.Section>
        )}
        {success && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccess('')}>{success}</Banner>
          </Layout.Section>
        )}

        {/* Retailer Accounts */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                {/* blockAlign="center": InlineStack aligns to the start of the cross axis by
                    default, so a short heading sat at the top of a row whose height was set by
                    the taller button. */}
                <InlineStack align="space-between" blockAlign="center">
                  <Text variant="headingMd" as="h2">Retailer Accounts</Text>
                  <Button variant="primary" onClick={() => openMpModal()}>
                    Add Retailer
                  </Button>
                </InlineStack>
                <Divider />
                {/* Columns and tips live in ACCOUNT_COLUMNS. The empty case is a ROW inside
                    this table rather than a replacement for it, so the headings survive and
                    a fresh install can see what adding an account will produce. */}
                <IndexTable
                  /* Follows the array actually rendered, and 1 when that is empty so the
                     heading row survives — which matters more now the headings are the sort
                     control, since an empty page would take the sort away with them. */
                  itemCount={itemCountFor(visibleMarketplaces)}
                  headings={headingsFor(ACCOUNT_COLUMNS)}
                  loading={loading}
                  selectable={false}
                  /* Sorting is client-side over the whole array — see ACCOUNT_SORT_VALUES.
                     sortToggleLabels is REQUIRED here, not decoration: without it Polaris
                     throws away every heading tooltip on a sortable table. */
                  sortable={ACCOUNT_SORTABLE}
                  sortToggleLabels={ACCOUNT_SORT_TOGGLE_LABELS}
                  sortColumnIndex={accountSortIndex ?? undefined}
                  sortDirection={accountSortDirection}
                  defaultSortDirection="ascending"
                  onSort={handleAccountSort}
                >
                  {visibleMarketplaces.length === 0 ? emptyRow(
                    ACCOUNT_COLUMNS, 'accounts',
                    error
                      ? 'Could not load retailer accounts — see the error above.'
                      : 'No retailer accounts yet. Add one to get started — each row will be one Mirakl account, with the brands, SKUs and orders it carries.',
                  ) : (
                    visibleMarketplaces.map((mp, i) => (
                      <IndexTable.Row id={mp.id} key={mp.id} position={i}>
                        <IndexTable.Cell>
                          <Text variant="bodyMd" fontWeight="semibold">{mp.name}</Text>
                          <Text variant="bodySm" tone="subdued">{mp.slug}</Text>
                          {/* What the retailer calls ITSELF, read from Mirakl on verify —
                              as opposed to the name above, which is what we typed. Worth
                              showing side by side: it is the confirmation that a key points
                              at the account you meant, and these are the exact values that
                              go onto every Shopify order this retailer sends, so a wrong
                              one is visible here before it is visible on an order.
                              Absent until the first successful verify. */}
                          {(mp.platformName || mp.tenantName) && (
                            <Text variant="bodySm" tone="subdued">
                              Mirakl: {mp.platformName || '—'}
                              {mp.tenantName ? ` · ${mp.tenantName}` : ''}
                            </Text>
                          )}
                          {/* The retailer's own zone, which is the zone its deadlines are set
                              in. Order dates are rendered in the STORE's zone, so these can
                              differ by a day — shown here so that is explainable. */}
                          {mp.platformTimezone && (
                            <Text variant="bodySm" tone="subdued">{mp.platformTimezone}</Text>
                          )}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {/* Falls back to the name in the display exactly as the tag builder
                              falls back to it, so what is shown is what orders will carry. */}
                          <Text variant="bodySm" fontWeight={mp.orderTagPrefix ? undefined : 'regular'}
                            tone={mp.orderTagPrefix ? undefined : 'subdued'}>
                            {mp.orderTagPrefix || mp.name}
                          </Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {mp.orderNamePrefix ? (
                            <Text variant="bodySm"><code>#{mp.orderNamePrefix}-</code></Text>
                          ) : (
                            <Tooltip content="Not set, so each brand's orders from this retailer keep the reference the retailer sends with them — unless that brand sets a prefix on its own connection.">
                              <Text variant="bodySm" tone="subdued">Order&rsquo;s own reference</Text>
                            </Tooltip>
                          )}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {!mp.tagResolution ? (
                            <Text variant="bodySm" tone="subdued">—</Text>
                          ) : mp.tagResolution.state === 'ok' ? (
                            <Tooltip content={`via ${mp.tagResolution.patternType} ${mp.tagResolution.patternValue}`}>
                              <Badge tone="success">{mp.tagResolution.marketplace}</Badge>
                            </Tooltip>
                          ) : mp.tagResolution.state === 'inactive' ? (
                            <Tooltip content="A pattern for this prefix exists but is switched off, so orders will not be attributed. Enable it on Marketplace Settings.">
                              <Badge tone="warning">Pattern inactive</Badge>
                            </Tooltip>
                          ) : (
                            <BlockStack gap="100">
                              <Tooltip content="Orders from this retailer will fall through to Others and bill at the wrong commission rate.">
                                <Badge tone="critical">No match → Others</Badge>
                              </Tooltip>
                              <Button
                                size="micro"
                                loading={fixingAccount === mp.id}
                                disabled={fixingAccount !== null}
                                onClick={() => handleAccountAttribution(mp.id)}
                              >
                                Create match
                              </Button>
                            </BlockStack>
                          )}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Badge tone={mp.isActive ? 'success' : undefined}>
                            {mp.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </IndexTable.Cell>
                        <IndexTable.Cell>{mp._count?.brandConnections ?? 0}</IndexTable.Cell>
                        <IndexTable.Cell>{mp.productCount ?? 0}</IndexTable.Cell>
                        <IndexTable.Cell>{mp._count?.skuMaps ?? 0}</IndexTable.Cell>
                        {/* Verification is automatic and daily now, so this reports a
                            state rather than offering a button. Three states, because
                            "never checked" and "checked and broken" need different
                            reactions: the first is a new account, the second is a
                            retailer that has stopped accepting our credentials. */}
                        <IndexTable.Cell>
                          {mp.lastVerifyError ? (
                            <Tooltip content={mp.lastVerifyError}>
                              <BlockStack gap="050">
                                <Badge tone="critical">Failing</Badge>
                                <Text variant="bodySm" tone="subdued">
                                  {mp.lastVerifiedAt
                                    ? `worked until ${new Date(mp.lastVerifiedAt).toLocaleDateString()}`
                                    : 'never worked'}
                                </Text>
                              </BlockStack>
                            </Tooltip>
                          ) : mp.lastVerifiedAt ? (
                            <BlockStack gap="050">
                              <Badge tone="success">Verified</Badge>
                              <Text variant="bodySm" tone="subdued">
                                {new Date(mp.lastVerifiedAt).toLocaleDateString()}
                              </Text>
                            </BlockStack>
                          ) : (
                            <Badge tone="attention">Not yet checked</Badge>
                          )}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <InlineStack gap="200" wrap>
                            {/* Reads first, then the write, then Edit — so the two safe buttons
                                are not adjacent to the destructive one, and Push inventory does
                                not sit where an eye expects Fetch. */}
                            {mp.isActive && Object.entries(ACCOUNT_ACTIONS).map(([key, action]) => {
                              // A write whose operation is switched off says so on the button
                              // instead of failing on click; the route refuses it regardless.
                              const unavailable = action.unavailable?.(mp) || null;
                              return (
                              <Tooltip key={key} content={unavailable || action.tooltip}>
                                <Button
                                  size="slim"
                                  tone={action.writes ? 'critical' : undefined}
                                  variant={action.writes ? 'secondary' : undefined}
                                  loading={runningAction === `${mp.id}:${key}`}
                                  disabled={Boolean(runningAction) || Boolean(unavailable)}
                                  onClick={() => runAccountAction(mp, key)}
                                >
                                  {action.label}
                                </Button>
                              </Tooltip>
                              );
                            })}
                            <Button size="slim" onClick={() => openMpModal(mp)}>Edit</Button>
                            {mp.isActive && (
                              <Button size="slim" tone="critical" onClick={() => handleDeactivate(mp.id)}>
                                Deactivate
                              </Button>
                            )}
                            {/* Last, after Deactivate, and offered whether or not the
                                account is active — it used to appear only on an
                                INACTIVE account, which meant the only route to deleting a
                                live one was to deactivate it first and hunt for the button
                                that then appeared. Kept visually quietest of the three
                                criticals (plain) because Deactivate is what almost everyone
                                reaching over here actually wants; the dialog says so too. */}
                            <Tooltip content="Permanently delete this retailer account and everything it holds. Deactivate instead if you only want to stop it syncing.">
                              <Button
                                size="slim"
                                variant="plain"
                                tone="critical"
                                onClick={() => openDeleteMarketplace(mp)}
                              >
                                Delete
                              </Button>
                            </Tooltip>
                          </InlineStack>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    ))
                  )}
                </IndexTable>

                {/* Both controls in one row, and only once there is something to page
                    through. The pager drops out when everything fits, but the row count
                    stays: it is how someone GETS a second page, so hiding it at size 50 would
                    strand them there. Labelled with `accountSafePage`, the value the rows were
                    actually sliced with — the raw page would read "Page 3 of 1" after a
                    deletion. */}
                {sortedMarketplaces.length > 0 && (
                  <InlineStack align="space-between" blockAlign="center">
                    <PageSizeSelect value={accountPageSize} onChange={setAccountPageSize} />
                    {accountTotalPages > 1 && (
                      <Pagination
                        hasPrevious={accountSafePage > 1}
                        onPrevious={() => setAccountPage(accountSafePage - 1)}
                        hasNext={accountSafePage < accountTotalPages}
                        onNext={() => setAccountPage(accountSafePage + 1)}
                        label={`Page ${accountSafePage} of ${accountTotalPages}`}
                      />
                    )}
                  </InlineStack>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Brands, directly below the accounts table.

            It answers the question the accounts table raises and cannot answer: this retailer
            is connected, but are the brands it actually sends offers for reachable? Finding
            that used to mean reading the SKU listing and inferring it from what was missing. */}
        <Layout.Section>
          <BrandConnectionTable
            onConfigure={(row) => navigate(`/brands/${row.brandId}?tab=connector`)}
          />
        </Layout.Section>

        {/* Retailer Catalogue, full width.

            Ahead of the two order tables because that is the setup order: the catalogue is
            mapped before orders flow, and an order that reached no brand is usually explained
            by a row here. It is also the only table that says what the retailer actually
            lists — the SKU map is Shopify-driven and inner-joins the brand connections, so a
            retailer with offers but no connection reads as "0 listed" there while holding
            thousands of rows. */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <BlockStack gap="050">
                  <Text variant="headingMd" as="h2">Retailer Catalogue</Text>
                  <Text variant="bodySm" tone="subdued">
                    What this retailer lists, as they sent it. Every offer we hold appears
                    here whether or not a brand is connected, so this is where a fetch is
                    confirmed and where offers that cannot be routed or fulfilled are found.
                  </Text>
                </BlockStack>
                <RetailerCatalogueTable marketplaces={marketplaces} />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* SKU Map, full width.

            Directly after Retailer Catalogue: the two catalogue tables are the pair, one per
            side, and the map is the comparison between them. Both sit above the order tables
            because mapping is what has to be right before an order can reach a brand.

            It shared a two-column grid with Queue while both were three counts and a button.
            It now carries a ten-column table, which does not fit half a row — so it took the
            full width, and the queue has since left this page altogether. */}
        <Layout.Section>
            <Card>
              <Box padding="400">
                <BlockStack gap="400">
                  {/* The explanation is a line under the heading, not a tooltip. It is the
                      first thing you need in order to read the table at all, and hiding what a
                      screen is FOR behind a hover is the wrong trade — a tooltip is for a
                      caveat on one number, not for the subject of the card. */}
                  <InlineStack align="space-between" blockAlign="start">
                    <BlockStack gap="050">
                      <Text variant="headingMd" as="h2">SKU Map</Text>
                      <Text variant="bodySm" tone="subdued">
                        The link between a brand&rsquo;s Shopify SKUs and the same items listed at a
                        retailer. An order or a stock change can only reach the right product if
                        that link exists, so anything unlinked shows here as not listed.
                      </Text>
                    </BlockStack>
                    {/* "Refresh" alone said nothing about what it refreshes, in which
                        direction, or whether it writes. Named for what it does and matched to
                        the button of the same name on a brand's tab, so the two screens do not
                        have differently-named buttons for one action. */}
                    <Tooltip content="Re-reads which of your SKUs each retailer lists, and at what stock and price — across every retailer account. Reads only; nothing is sent to a retailer. It does not re-read the Shopify side, so the two figures in the table will be of different ages until the nightly catalogue sync runs.">
                      <Button onClick={handleSkuRefresh} loading={refreshing} size="slim">
                        Refresh SKU Map
                      </Button>
                    </Tooltip>
                  </InlineStack>
                  <Divider />

                  {/* No Total SKUs / Brands pair here.
                
                      Both came from getStats() with no account scope, so they summed every
                      retailer's SKU map and counted distinct retailer brand spellings across
                      all of them. With one account that read as a fact about that account;
                      with three it is a fact about nothing, and the brand count in particular
                      would merge one brand's differing spellings across retailers into a
                      single tally nobody can act on.
                
                      The same two figures, scoped to one retailer and split by whether each
                      brand actually routes, are in Retailer Catalogue above. The brand tab
                      keeps its copy: there getStats is passed a brandId and the numbers are
                      about that brand. */}

                  {/* The counts say how much is listed; the table says whether the stock
                      agrees. Same component as a brand's Retail Connect tab, with the Brand
                      column and brand filter this screen needs because it spans every brand. */}
                  <SkuMapTable
                    showBrandColumn
                    brandOptions={brandFilterOptions}
                    marketplaceOptions={retailerFilterOptions}
                    refreshToken={skuRowsToken}
                    pushDisabledReason={inventoryPushOff}
                  />
                </BlockStack>
              </Box>
            </Card>
        </Layout.Section>

        {/* Retailer Orders — what the retailer sent, before it is matched to a brand.

            First of the two ORDER tables, because it is the first thing that happens to an
            order. Fetching stores the order whether
            or not a brand can be matched, so this is the complete record and the only place an
            order no brand received is visible.

            Directly above Orders to Shopify because it is the same orders from the other side.
            That table lists the per-brand slices — what a brand RECEIVED. This one lists what
            ARRIVED, stored before any matching ran, so an order no brand received is visible
            here instead of being absent from both. */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Retailer Orders</Text>
                <StoredRetailerOrdersTable marketplaces={marketplaces} />
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Orders to Shopify — the per-brand slices.

            SECOND, after Retailer Orders above, because that is the sequence: the retailer
            sends an order, matching splits it per brand, and only then can a Shopify order
            exist. Reading the result before the thing that produces it was what made two
            sections called "Retailer Orders" confusing.

            One row per BRAND per retailer order, which is why a retailer order carrying two
            brands' lines appears twice here and once above. */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">Orders to Shopify ({globalOrders.total})</Text>
                <InlineStack gap="300" wrap>
                  <Box width="260px">
                    <TextField
                      label="Search orders"
                      labelHidden
                      placeholder="Retailer order id, customer, tracking…"
                      value={orderSearchInput}
                      onChange={setOrderSearchInput}
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => setOrderSearchInput('')}
                    />
                  </Box>
                  <Box width="200px">
                    <Select
                      label="Marketplace"
                      labelHidden
                      options={mpOptions}
                      value={orderFilter.marketplace}
                      onChange={(v) => setOrderFilter((f) => ({ ...f, marketplace: v }))}
                    />
                  </Box>
                  <Box width="200px">
                    <Select
                      label="Brand"
                      labelHidden
                      options={brandOptions}
                      value={orderFilter.brand}
                      onChange={(v) => setOrderFilter((f) => ({ ...f, brand: v }))}
                    />
                  </Box>
                  <Box width="160px">
                    <Select
                      label="Status"
                      labelHidden
                      options={statusOptions}
                      value={orderFilter.status}
                      onChange={(v) => setOrderFilter((f) => ({ ...f, status: v }))}
                    />
                  </Box>
                </InlineStack>
                <Divider />
                {/* Columns and tips live in GLOBAL_ORDER_COLUMNS. As above, the empty case is
                    a row inside the table so the headings stay put — and the copy separates
                    "no orders exist" from "your filters exclude them all", which the old
                    "No orders found." could not. */}
                <IndexTable
                  itemCount={globalOrders.orders.length || 1}
                  headings={headingsFor(GLOBAL_ORDER_COLUMNS)}
                  loading={loading}
                  selectable={false}
                  sortable={ORDER_SORTABLE}
                  sortToggleLabels={ORDER_SORT_TOGGLE_LABELS}
                  sortColumnIndex={orderSortIndex ?? undefined}
                  sortDirection={orderSortDirection}
                  defaultSortDirection="descending"
                  onSort={(index, direction) => {
                    setOrderSortIndex(index);
                    setOrderSortDirection(direction);
                  }}
                >
                  {globalOrders.orders.length === 0 ? emptyRow(
                    GLOBAL_ORDER_COLUMNS, 'orders',
                    error
                      ? 'Could not load retailer orders — see the error above.'
                      // '' is "all" for each of these three (state declared above), so any
                      // non-empty value means the admin has narrowed the list themselves.
                      : (orderFilter.marketplace || orderFilter.brand || orderFilter.status
                        || orderFilter.search.trim())
                        ? 'No orders match these filters. Search covers the retailer order id, Mirakl\'s commercial id, the customer reference, the Shopify order id, the tracking number, and the customer name or email — but not SKUs, which are in the SKU Map.'
                        : 'No orders fetched from a retailer yet. Each row will be one retailer order — this brand’s slice of it — and whether it reached Shopify.',
                  ) : (
                    globalOrders.orders.map((order, i) => (
                      <IndexTable.Row
                        id={order.id}
                        key={order.id}
                        position={i}
                        onClick={() => navigate(`/brands/${order.brandId}?tab=connector`)}
                      >
                        <IndexTable.Cell>
                          <Text variant="bodyMd" fontWeight="semibold">{order.miraklOrderId}</Text>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          <Badge>{order.marketplace?.name || '—'}</Badge>
                        </IndexTable.Cell>
                        <IndexTable.Cell>{order.brand?.name || '—'}</IndexTable.Cell>
                        <IndexTable.Cell>
                          {order.status === 'FAILED' && order.error ? (
                            <Tooltip content={order.error} preferredPosition="above">
                              <Badge tone="critical">FAILED</Badge>
                            </Tooltip>
                          ) : (
                            <Badge tone={
                              order.status === 'SYNCED' ? 'success' :
                              order.status === 'FAILED' ? 'critical' :
                              order.status === 'PENDING' ? 'attention' : undefined
                            }>
                              {order.status}
                            </Badge>
                          )}
                        </IndexTable.Cell>
                        <IndexTable.Cell>{order.miraklStatus}</IndexTable.Cell>
                        <IndexTable.Cell>
                          {order.shopifyOrderId && order.brand?.shopDomain ? (
                            <a
                              href={`https://admin.shopify.com/store/${order.brand.shopDomain.replace(/\.myshopify\.com$/i, '')}/orders/${order.shopifyOrderId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Text variant="bodySm">{order.shopifyOrderId}</Text>
                            </a>
                          ) : (
                            <Text variant="bodySm" tone="subdued">
                              {order.status === 'FAILED' ? 'not created' : '—'}
                            </Text>
                          )}
                        </IndexTable.Cell>
                        <IndexTable.Cell>{order.lineItemCount ?? 0}</IndexTable.Cell>
                        {/* Currency comes from the order row (MiraklOrder.currencyCode,
                            schema.prisma:1059) instead of a hardcoded '$' - these are
                            marketplace orders and the retailer is not always USD. */}
                        <IndexTable.Cell>
                          {apiClient.formatCurrency(order.totalPrice ?? 0, order.currencyCode || undefined)}
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                          {/* miraklCreatedDate, NOT createdAt. createdAt is our own insert
                              time, so on a backfill every row read as today while the orders
                              spanned two years — and this column's tooltip explicitly denies
                              being the import time. No fallback to createdAt: substituting it
                              would restore exactly the figure the tooltip disclaims. */}
                          {order.miraklCreatedDate
                            ? new Date(order.miraklCreatedDate).toLocaleDateString()
                            : <Text tone="subdued">—</Text>}
                        </IndexTable.Cell>
                        {/* Only the action this order's stage actually allows, so a button is
                            never a write that cannot apply. stopPropagation on each: the row
                            itself navigates to the brand's tab. */}
                        <IndexTable.Cell>
                          <InlineStack gap="100" wrap={false}>
                            {order.miraklStatus === 'WAITING_ACCEPTANCE' && (
                              <Tooltip content={acceptOffFor(order) || 'Accepts this order on the retailer, committing you to fulfil it. Writes to Mirakl.'}>
                                <Button
                                  size="micro"
                                  tone="critical"
                                  loading={orderActionId === order.id}
                                  disabled={orderActionId !== null || Boolean(acceptOffFor(order))}
                                  onClick={(e) => { e.stopPropagation(); runOrderAction(order, 'accept'); }}
                                >
                                  Accept
                                </Button>
                              </Tooltip>
                            )}
                            {!order.shopifyOrderId && !order.shopifyOrderGid
                              && ['SHIPPING', 'SHIPPED', 'RECEIVED'].includes(order.miraklStatus) && (
                                <Tooltip content={shopifyOrdersOffFor(order) || 'Creates the Shopify order for this one. Writes to Shopify, not to the retailer.'}>
                                  <Button
                                    size="micro"
                                    loading={orderActionId === order.id}
                                    disabled={orderActionId !== null || Boolean(shopifyOrdersOffFor(order))}
                                    onClick={(e) => { e.stopPropagation(); runOrderAction(order, 'shopify'); }}
                                  >
                                    {order.status === 'FAILED' ? 'Retry' : 'Push to Shopify'}
                                  </Button>
                                </Tooltip>
                              )}
                            {order.trackingNumber && !order.trackingPushedAt && (
                              <Tooltip content={trackingOffFor(order) || 'Tells the retailer this order shipped, with its tracking number. Writes to Mirakl.'}>
                                <Button
                                  size="micro"
                                  tone="critical"
                                  loading={orderActionId === order.id}
                                  disabled={orderActionId !== null || Boolean(trackingOffFor(order))}
                                  onClick={(e) => { e.stopPropagation(); runOrderAction(order, 'tracking'); }}
                                >
                                  Push tracking
                                </Button>
                              </Tooltip>
                            )}
                            {order.miraklStatus !== 'WAITING_ACCEPTANCE'
                              && (order.shopifyOrderId || order.shopifyOrderGid)
                              && !(order.trackingNumber && !order.trackingPushedAt) && (
                                <Text variant="bodySm" tone="subdued">&mdash;</Text>
                              )}
                          </InlineStack>
                        </IndexTable.Cell>
                      </IndexTable.Row>
                    ))
                  )}
                </IndexTable>

                {/* Real paging, driven by the API's own page/totalPages — the row count goes
                    out as its `limit`, so changing it refetches rather than slicing. The
                    existing Previous/Next stay as they are; the selector sits beside them, and
                    remains when the pager drops out because everything fits. */}
                {globalOrders.orders.length > 0 && (
                  <InlineStack align="space-between" blockAlign="center">
                    <PageSizeSelect value={orderPageSize} onChange={setOrderPageSize} />
                    {globalOrders.totalPages > 1 && (
                      <InlineStack align="center" blockAlign="center" gap="300">
                        <Button
                          onClick={() => setOrderPage((p) => Math.max(1, p - 1))}
                          disabled={orderPage <= 1}
                        >
                          Previous
                        </Button>
                        <Text variant="bodySm" tone="subdued">
                          Page {globalOrders.page || orderPage} of {globalOrders.totalPages}
                          {' · '}
                          {globalOrders.total} order{globalOrders.total === 1 ? '' : 's'}
                        </Text>
                        <Button
                          onClick={() => setOrderPage((p) => p + 1)}
                          disabled={orderPage >= globalOrders.totalPages}
                        >
                          Next
                        </Button>
                      </InlineStack>
                    )}
                  </InlineStack>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* The Queue and Sync History cards MOVED to the Background Jobs section in the
            sidebar (/queue and /sync-history). Neither was scoped to anything on this page:
            both show every job on every retailer account, and sitting below the accounts
            list, the SKU map and the orders table they were the longest scroll in the
            console to "is anything stuck?". The tables themselves are unchanged — the same
            two components, still passed no brandId. */}

      </Layout>


      {/* Add/Edit Marketplace Modal */}
      <Modal
        open={mpModalOpen}
        onClose={() => setMpModalOpen(false)}
        title={editingMpId ? 'Edit Retailer' : 'Add Retailer'}
        // Wider, because this is where a retailer's settings live and there are more of them
        // now. Kept a modal rather than promoted to a page: a page is the better home if this
        // keeps growing, but that is a bigger change than widening what is already here.
        size="large"
        primaryAction={
          // On add, the primary action IS the connect step until it has succeeded. There is
          // nothing to create before then — the name comes from the account itself.
          editingMpId || mpProbe
            ? {
                content: editingMpId ? 'Save' : 'Create',
                onAction: handleMpSave,
                loading: mpSaving,
                disabled: !mpForm.name || !mpForm.apiUrl || (!editingMpId && !mpForm.apiKey),
              }
            : {
                content: 'Connect',
                onAction: handleMpProbe,
                loading: mpProbing,
                disabled: !mpForm.apiUrl || !mpForm.apiKey,
              }
        }
        secondaryActions={[
          // Without this, a probe against the wrong key is a dead end: the credential fields
          // are locked to whatever was identified, so the only way back was Cancel and retype.
          ...(!editingMpId && mpProbe
            ? [{
                content: 'Use different credentials',
                onAction: () => {
                  setMpProbe(null);
                  setMpProbeError('');
                  setMpForm((f) => ({ ...f, name: '', slug: '' }));
                },
              }]
            : []),
          { content: 'Cancel', onAction: () => setMpModalOpen(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="API URL"
              value={mpForm.apiUrl}
              onChange={(v) => setMpForm((f) => ({ ...f, apiUrl: v }))}
              autoComplete="off"
              placeholder="https://marketplace.mirakl.net/api"
              disabled={!!mpProbe}
            />
            <TextField
              label="API Key"
              value={mpForm.apiKey}
              onChange={(v) => setMpForm((f) => ({ ...f, apiKey: v }))}
              autoComplete="off"
              type="password"
              placeholder={editingMpId ? 'Leave blank to keep current key' : ''}
              helpText={editingMpId ? 'Clear and enter a new key to change it' : ''}
              disabled={!!mpProbe}
            />

            {/* Step one of adding an account looks like the whole form, which is why the
                sync options read as missing: there is no sign that a second step exists.
                Says what arrives next and that nothing is written yet, so Connect is
                obviously safe to press. Add only, and only before the probe answers — and not
                while an error is showing, since "Connect first" above "Could not connect" is
                two blocks of prose competing for the one thing to act on. */}
            {!editingMpId && !mpProbe && !mpProbeError && (
              <Text variant="bodySm" tone="subdued">
                Connect first — the retailer&apos;s name, order tag and sync options
                (orders, inventory, tracking) appear here once these credentials answer.
                Nothing is saved until you press Create.
              </Text>
            )}

            {/* Add only. Everything below appears once Mirakl has answered — including the
                name, which is the retailer's own and not something to be typed. */}
            {!editingMpId && mpProbeError && (
              <Banner tone="critical" title="Could not connect">
                <p>{mpProbeError}</p>
              </Banner>
            )}

            {!editingMpId && mpProbe && (
              // BlockStack, not a bare fragment. FormLayout spaces its direct children and a
              // fragment is one child, so the banners and both fields shared a single slot with
              // no spacing between them. An explicit gap is also more honest here than relying
              // on FormLayout's, since this group mixes banners with fields.
              <BlockStack gap="400">
                <Banner
                  tone={mpProbe.platformName ? 'success' : 'warning'}
                  title={mpProbe.platformName
                    ? `Connected to ${mpProbe.platformName}`
                    : 'Connected, but this account did not identify itself'}
                >
                  <BlockStack gap="100">
                    <Text variant="bodySm">
                      {mpProbe.totalOffers} offer{mpProbe.totalOffers === 1 ? '' : 's'} visible
                      {mpProbe.tenantName ? ` · Tenant ${mpProbe.tenantName}` : ''}
                      {mpProbe.platformTimezone ? ` · ${mpProbe.platformTimezone}` : ''}
                    </Text>
                    {/* Stated as the retailer's zone, not ours. Order dates are shown in the
                        store's time zone to match how they have always been imported, so a
                        deadline here can read a day later than the retailer's own cut-off. */}
                    {mpProbe.platformTimezone && (
                      <Text variant="bodySm" tone="subdued">
                        This retailer&apos;s deadlines are set in {mpProbe.platformTimezone}.
                      </Text>
                    )}
                    {!mpProbe.platformName && (
                      <Text variant="bodySm" tone="subdued">
                        The credentials work, but this retailer did not return a platform name.
                        Enter one below — it will be used as the order tag.
                      </Text>
                    )}
                  </BlockStack>
                </Banner>

                {/* A real case, not a double-click guard: two shops on one operator return
                    the same platform_name, and the tenant is what separates them. */}
                {mpProbe.duplicate && (
                  <Banner tone="warning" title="This retailer is already connected">
                    <p>
                      An account named &quot;{mpProbe.duplicate.name}&quot;
                      {mpProbe.duplicate.tenantName ? ` (tenant ${mpProbe.duplicate.tenantName})` : ''}
                      {' '}already exists{mpProbe.duplicate.isActive ? '' : ', and is currently inactive'}.
                      Give this one a different name if it is a separate shop.
                    </p>
                  </Banner>
                )}

                <TextField
                  label="Marketplace Name"
                  value={mpForm.name}
                  onChange={(v) => setMpForm((f) => ({ ...f, name: v }))}
                  autoComplete="off"
                  helpText="Taken from the retailer."
                />
                <TextField
                  label="Order Tag"
                  value={mpForm.orderTagPrefix}
                  onChange={(v) => setMpForm((f) => ({ ...f, orderTagPrefix: v }))}
                  autoComplete="off"
                  helpText="The tag every order from this retailer carries in Shopify, and what billing matches to attribute it. Defaults to the name above."
                />
                <TextField
                  label="Order Name Prefix"
                  value={mpForm.orderNamePrefix}
                  onChange={(v) => setMpForm((f) => ({ ...f, orderNamePrefix: v }))}
                  autoComplete="off"
                  placeholder="e.g. SPO then Order name #SPO-MiraklOrderId"
                  helpText={ORDER_NAME_PREFIX_HELP}
                />
              </BlockStack>
            )}

            {/* Edit keeps a plain name field: the account is already identified, and what
                Mirakl reports is shown in the table beside it. */}
            {/* Two separate conditionals rather than one fragment holding both.
            
                FormLayout spaces its DIRECT children, and a fragment counts as one child — so
                both fields landed in a single slot with nothing between them while the fields
                above, which are direct children, were spaced normally. */}
            {editingMpId && (
              <TextField
                label="Marketplace Name"
                value={mpForm.name}
                onChange={(v) => setMpForm((f) => ({ ...f, name: v }))}
                autoComplete="off"
              />
            )}
            {editingMpId && (
              <TextField
                label="Order Tag"
                value={mpForm.orderTagPrefix}
                onChange={(v) => setMpForm((f) => ({ ...f, orderTagPrefix: v }))}
                autoComplete="off"
                helpText="The tag every order from this retailer carries in Shopify, and what billing matches to attribute it. Changing it creates a pattern for the new tag; the old one is left alone, because past orders still carry it."
              />
            )}
            {editingMpId && (
              <TextField
                label="Order Name Prefix"
                value={mpForm.orderNamePrefix}
                onChange={(v) => setMpForm((f) => ({ ...f, orderNamePrefix: v }))}
                autoComplete="off"
                placeholder="e.g. SPO then Order name #SPO-MiraklOrderId"
                helpText={`${ORDER_NAME_PREFIX_HELP} Changing it renames FUTURE orders only — orders already in Shopify keep the names they were given.`}
              />
            )}
            {/* Which syncs run for this retailer. ONE copy of this group, shared by Add and
                Edit, gated the same way the modal's primary action is.

                It was rendered twice — once inside the post-probe block above and once here,
                unconditionally — so adding an account showed six checkboxes driving three
                pieces of state. Gating on the same `editingMpId || mpProbe` as the primary
                action is what keeps them in step: this group is part of "confirm the account",
                which does not exist until credentials have answered.

                Not three identical checkboxes: two of them lose money when off, and the help
                text says which and how. The consequence sits next to the box rather than in a
                tooltip, because it is the thing that decides whether to tick it. */}
            {(editingMpId || mpProbe) && (
              // BlockStack for the same reason as the probe group: FormLayout spaces its
              // direct children and a fragment is one child, so all three boxes would share
              // a single slot.
              <BlockStack gap="300">
                {/* `!== false`, not the bare value. Absent means on — that is the rule the
                    create route and the worker gate both use — so the form must read an
                    undefined flag as ticked rather than silently offering to switch a sync
                    off. Without it a form whose state predates these fields renders three
                    unticked boxes and saves an account with everything disabled. */}
                {/* Above the switches, because it bounds what "Sync orders" will do the
                    first time it runs rather than being a preference alongside it. */}
                <TextField
                  label="Fetch orders created after"
                  type="date"
                  value={mpForm.ingestOrdersCreatedAfter || ''}
                  onChange={(v) => setMpForm((f) => ({ ...f, ingestOrdersCreatedAfter: v }))}
                  autoComplete="off"
                  helpText="Orders created before this date are never fetched from this retailer. Leave it empty to fetch the retailer's entire order history — on an established account that can be thousands of already-delivered orders, each of which becomes a Shopify order and a stock decrement once order creation is switched on. Changing it later does not remove orders already collected."
                />
                <Checkbox
                  label="Sync orders"
                  checked={mpForm.syncOrders !== false}
                  onChange={(v) => setMpForm((f) => ({ ...f, syncOrders: v }))}
                  helpText="Off stops collecting orders AND accepting them. On a live retailer that means missing the acceptance window, and the retailer cancels the order."
                />
                {/* ACCEPTANCE. Per retailer, because the retailers differ on whether a
                    seller has to accept at all — and what THEY require is shown beside it,
                    read from their own platform settings on verify. */}
                {acceptanceNote(mpForm.orderAcceptanceMode) === null ? (
                  <Checkbox
                    label="Accept orders on this retailer"
                    checked={mpForm.acceptOrders === true}
                    onChange={(v) => setMpForm((f) => ({ ...f, acceptOrders: v }))}
                    helpText={acceptanceHelp(mpForm.orderAcceptanceMode)}
                  />
                ) : (
                  <Banner tone="info">
                    <Text variant="bodySm">{acceptanceNote(mpForm.orderAcceptanceMode)}</Text>
                  </Banner>
                )}
                <Checkbox
                  label="Create Shopify orders from this retailer"
                  checked={mpForm.createShopifyOrders === true}
                  onChange={(v) => setMpForm((f) => ({ ...f, createShopifyOrders: v }))}
                  helpText="Turns this retailer's orders into real orders in the brand's Shopify store, once the retailer has accepted and paid for them. That decrements stock and flows into billing, so it is off until you turn it on."
                />
                <Checkbox
                  label="Sync inventory"
                  checked={mpForm.syncInventory !== false}
                  onChange={(v) => setMpForm((f) => ({ ...f, syncInventory: v }))}
                  helpText="Off leaves stock levels stale at the retailer, which risks selling something you no longer have."
                />
                <Checkbox
                  label="Sync tracking"
                  checked={mpForm.syncTracking !== false}
                  onChange={(v) => setMpForm((f) => ({ ...f, syncTracking: v }))}
                  helpText="Off means the retailer is never told the order shipped. On pay-on-shipment terms they will not pay."
                />
              </BlockStack>
            )}

          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* ---- Delete retailer account ---------------------------------------------------
          The most destructive action in this console, so the dialog is built to be READ:
          it names the account, states the real counts about to be destroyed, separates
          what can be re-fetched from what cannot, and points at Deactivate for the person
          who only meant to stop the syncing. Typing the name is the gate, because the
          realistic failure is a mis-click on a row of small buttons rather than a
          considered decision someone regrets. */}
      <Modal
        open={deleteTarget != null}
        onClose={closeDeleteMarketplace}
        title={`Delete ${deleteTarget?.name || 'retailer account'}?`}
        primaryAction={{
          content: 'Delete this account',
          destructive: true,
          loading: deleting,
          disabled: Boolean(deleteBlockedReason) || deleting,
          onAction: handleDeleteMarketplace,
        }}
        secondaryActions={[
          { content: 'Cancel', onAction: closeDeleteMarketplace, disabled: deleting },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {/* The alternative FIRST, before the scary list. Someone who reached for Delete
                meaning Deactivate should find that out at the top of the dialog, not after
                reading past the part that tells them how much they are about to lose. */}
            <Banner tone="info">
              <Text variant="bodyMd">
                If you only want to stop this account syncing, use <b>Deactivate</b> instead.
                Deactivate keeps every order, SKU mapping and connection exactly as it is, and
                can be undone. Delete cannot.
              </Text>
            </Banner>

            {deleteConnectedCount > 0 && (
              <Banner tone="warning">
                <Text variant="bodyMd">
                  This account still has {deleteConnectedCount} connected
                  {deleteConnectedCount === 1 ? ' store' : ' stores'}. The server refuses to
                  delete an account with any store connected, so disconnect them first.
                </Text>
              </Banner>
            )}

            {deleteStatsError && (
              <Banner tone="critical">
                <Text variant="bodyMd">
                  Could not read what this account holds: {deleteStatsError} Deleting is
                  blocked while that is unknown — close this and try again.
                </Text>
              </Banner>
            )}

            {/* THE QUANTITY. A confirmation that says "this cannot be undone" without saying
                how much is being undone is a speed bump, not a confirmation. */}
            <BlockStack gap="150">
              <Text variant="headingSm" as="h3">What will be destroyed</Text>
              {deleteStatsLoading ? (
                <InlineStack gap="200" blockAlign="center">
                  <Spinner size="small" />
                  <Text variant="bodySm" tone="subdued">Counting what this account holds…</Text>
                </InlineStack>
              ) : deleteStats ? (
                <BlockStack gap="050">
                  <Text variant="bodyMd">
                    <b>{deleteStats.orderCount ?? 0}</b> stored order
                    {deleteStats.orderCount === 1 ? '' : 's'} from this retailer
                    {(deleteStats.ordersToday ?? 0) > 0
                      ? ` (${deleteStats.ordersToday} of them arrived in the last 24 hours)`
                      : ''}
                  </Text>
                  <Text variant="bodyMd">
                    <b>{deleteStats.skuCount ?? 0}</b> SKU map row
                    {deleteStats.skuCount === 1 ? '' : 's'}
                  </Text>
                  <Text variant="bodyMd">
                    <b>{deleteStats.brandCount ?? 0}</b> enabled brand connection
                    {deleteStats.brandCount === 1 ? '' : 's'}
                    {deleteConnectedCount !== (deleteStats.brandCount ?? 0)
                      ? ` (${deleteConnectedCount} in total, counting disabled ones)`
                      : ''}
                  </Text>
                </BlockStack>
              ) : (
                <Text variant="bodySm" tone="subdued">Counts unavailable.</Text>
              )}
            </BlockStack>

            <Divider />

            {/* The full cascade, in plain words. Eight tables follow this record down. */}
            <BlockStack gap="150">
              <Text variant="headingSm" as="h3">Everything that goes with it</Text>
              <Text variant="bodySm" tone="subdued">
                Deleting the account deletes, in the same operation: its brand connections;
                every per-brand order slice; every raw order we stored from this retailer; its
                whole SKU map; its sync job history; any queued stock pushes; its stock import
                records; and its cached list of accepted carriers. The credentials
                stored on the account go too.
              </Text>
            </BlockStack>

            <Banner tone="critical">
              <BlockStack gap="150">
                <Text variant="bodyMd" fontWeight="semibold">Some of this cannot be recovered.</Text>
                <Text variant="bodySm">
                  Re-adding the retailer later re-fetches only what Mirakl still returns.
                  Orders older than Mirakl’s fetch window are gone for good — and the
                  account’s last-sync watermark and its order-creation cutoff are deleted with
                  it, so there is no record of where the previous fetching had reached. You would
                  also re-enter the API credentials by hand and re-create every brand connection.
                </Text>
              </BlockStack>
            </Banner>

            <TextField
              label={`Type ${deleteExpectedName} to confirm`}
              value={deleteTyped}
              onChange={setDeleteTyped}
              autoComplete="off"
              disabled={deleting}
              placeholder={deleteExpectedName}
              error={
                deleteTyped.length > 0 && !deleteNameMatches
                  ? 'This does not match the account name yet.'
                  : undefined
              }
              helpText={`Case-sensitive, and with no extra spaces — it must read exactly ${deleteExpectedName}. The name is on the row you clicked; typing it is here so a mis-click cannot delete an account.`}
            />

            {deleteBlockedReason && (
              <Text variant="bodySm" tone="subdued">{deleteBlockedReason}</Text>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
